import {css} from 'aphrodite';
import React, {useState} from 'react';
import {NodeProps} from 'reactflow';

import getTestProps from 'test/lib/cypress/getTestProps';

import {
  NODE_WRAPPER_STYLES,
  NODE_STATUS_STYLES,
  NODE_STYLES,
  NODE_HEADER_STYLES,
} from './BenchlingNodeDesignConstants';
import {BenchlingNodeData, NodeStatus} from './BenchlingNodeTypes';
import {BenchlingNodeHeader} from './sub-components/BenchlingNodeHeader';
import {BenchlingNodeSections} from './sub-components/BenchlingNodeSections';
import {BenchlingNodeSectionSummaries} from './sub-components/BenchlingNodeSectionSummaries';

export type BenchlingNodeProps<
  TRowData,
  TNodeData extends BenchlingNodeData<TRowData>,
> = NodeProps<TNodeData>;

/**
 * Renders a base ReactFlow node with a header, section summaries and sections.
 *
 * This component serves as a starting point for creating custom nodes in ReactFlow,
 * allowing developers to define their own../SimpleBenchlingNode/sub-components/SimpleBenchlingNodeHandleng a consistent
 * design, style and interaction patterns across Benchling applications.
 *
 * See SimpleBenchlingNode for an out-of-the-box implementation of this component with a built-in
 * row renderer.
 *
 * Not sure whether to use SimpleBenchlingNode or BenchlingNode?
 * 1. Does DefaultRowData work for your use case? If no -> must use BenchlingNode, otherwise go to 2
 * 2. Do you want the default row renderer? If yes -> SimpleBenchlingNode, otherwise go to 3
 * 3. Do you want the default node handle renderer? If yes -> SimpleBenchlingNode, otherwise can use either component
 *
 * See BenchlingNode.stories.tsx in storybook for examples of how this component can change displays.
 * See JellyfishBenchlingNode.stories.tsx in storybook for examples of how to extend this base class.
 *
 * @example
 * interface MyRowData { id: string; value: number }
 * type MyNodeData = BenchlingNodeData<MyRowData>
 *
 * const nodeData: MyNodeData = {
 *   nodeName: "Custom Node",
 *   sections: [{ sectionName: "Data", sectionRows: [{ id: "row1", value: 42 }] }],
 *   rowRenderer: (rowData) => <div>{rowData.value}</div>,
 *   // Custom node handle rendering
 *   nodeHeaderHandleRenderer: (props) => props.handleType === HandleType.SOURCE ? <Handle {...props} /> : null,
 * };
 *
 * function MyNode(props: BenchlingNodeProps<MyRowData, MyNodeData>) {
 *   return <BenchlingNode {...props} />;
 * }
 */
export default function BenchlingNode<TRowData, TNodeData extends BenchlingNodeData<TRowData>>(
  props: BenchlingNodeProps<TRowData, TNodeData>
): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div
      className={css(NODE_WRAPPER_STYLES.benchlingNodeWrapper)}
      {...getTestProps({
        component: 'BenchlingNode',
        element: 'root',
      })}
    >
      <div
        className={css(
          NODE_WRAPPER_STYLES.benchlingNode,
          props.data.nodeStatus === NodeStatus.WARNING && NODE_STATUS_STYLES.warningBorder,
          props.data.nodeStatus === NodeStatus.ERROR && NODE_STATUS_STYLES.errorBorder,
          isHovered &&
            props.data.nodeStatus &&
            ![NodeStatus.ERROR, NodeStatus.WARNING].includes(props.data.nodeStatus) &&
            NODE_STYLES.hoverBorder,
          isHovered && NODE_STYLES.nodeHovered,
          props.dragging && NODE_STYLES.nodeDragging,
          props.selected && NODE_STYLES.nodeSelected,
          props.data.disabled && NODE_STYLES.disabled
        )}
        {...getTestProps({
          component: 'BenchlingNode',
          element: 'benchlingNode',
        })}
      >
        <div
          className={css(
            !(props.data.disabled || !props.isConnectable) && NODE_HEADER_STYLES.interactiveNodeHeader
          )}
          onMouseEnter={() => {
            setIsHovered(true);
          }}
          onMouseLeave={() => {
            setIsHovered(false);
          }}
        >
          <BenchlingNodeHeader {...props} />
        </div>
        <BenchlingNodeSectionSummaries {...props} />
        <BenchlingNodeSections {...props} />
      </div>
    </div>
  );
}
